import { observer } from 'mobx-react-lite'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { Button, Dropdown, Modal } from 'antd'
import type { MenuProps } from 'antd'
import {
    AndroidFilled,
    AppleFilled,
    BellFilled,
    CheckCircleFilled,
    ClockCircleFilled,
    DeleteOutlined,
    EditOutlined,
    GiftOutlined,
    LinkOutlined,
    MobileOutlined,
    MoreOutlined,
    PlusOutlined,
} from '@ant-design/icons'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import pushesController from './pushes.controller'
import { moment_custom } from '../../../helpers/timestamp/datetime.helper'
import { getPrice, getPriceShort } from '../../../helpers/price'
import __select from '../../../helpers/select_entity/__select.controller'
import { pushesFunnel, PUSH_FUNNEL_ENABLED } from './push_funnel.controller'
import { formatSince, percent, PushFunnelSummary } from './PushFunnel'
import { CardList, EMPTY_VALUE, ListCard, ListChip, ListMedia, ListMetricProps } from '../../../components/card_list/CardList'
import './pushes.less'

const referLabel = (refer: any): string | null => {
    if (!refer || !refer.type) return null
    return __select.types.find((type: any) => type.value === refer.type)?.label ?? refer.type
}

const DevicesChip = ({ devices }: { devices: string }) => {
    if (devices === 'ios') return <ListChip icon={<AppleFilled />}>iOS</ListChip>
    if (devices === 'android') return <ListChip icon={<AndroidFilled />}>Android</ListChip>
    return <ListChip icon={<MobileOutlined />}>{i.t('pushes:push.form.devices_all')}</ListChip>
}

// Карточка наблюдает воронку сама: цифры по рассылкам текущей страницы приходят отдельным запросом
const PushCard = observer(({ push }: { push: any }) => {
    const stats = pushesFunnel.stats(push.id)
    const scheduled = !push.sent && push.postpone_to > dayjs().unix()
    const editable = push.postpone_to > dayjs().unix()
    const image = push.image && push.image.uri ? `${push.image.base ?? ''}${push.image.uri}` : null
    const link = referLabel(push.refer)
    const ios = push.count_sent_ios ?? 0
    const android = push.count_sent_android ?? 0
    const delivered = stats ? stats.delivered : (push.sent ? ios + android : null)

    // soon — воронка в разработке; before — рассылка ушла до запуска воронки и цифр по ней не будет
    const trackingSince = pushesFunnel.trackingSince
    const sentBeforeFunnel = push.sent && !stats && (trackingSince === null || (typeof trackingSince === 'number' && push.postpone_to < trackingSince))
    const funnelState: 'soon' | 'before' | 'stats' = !PUSH_FUNNEL_ENABLED ? 'soon' : sentBeforeFunnel ? 'before' : 'stats'

    // Сетка метрик в 12 колонок: первая строка — доставка (3 ячейки по 4), вторая — воронка (4 ячейки по 3)
    const metrics: ListMetricProps[] = [
        {
            value: delivered !== null ? delivered.toLocaleString() : EMPTY_VALUE,
            label: i.t('pushes:funnel.delivered'),
            hint: i.t('pushes:funnel.deliveredHint'),
            span: 4,
        },
        { value: push.sent ? ios.toLocaleString() : EMPTY_VALUE, label: 'iOS', hint: i.t('pushes:funnel.deliveredIosHint'), span: 4 },
        { value: push.sent ? android.toLocaleString() : EMPTY_VALUE, label: 'Android', hint: i.t('pushes:funnel.deliveredAndroidHint'), span: 4 },
    ]

    // Заглушки занимают всю строку воронки
    if (funnelState === 'soon') metrics.push({
        value: i.t('pushes:funnel.soon'),
        label: i.t('pushes:list.soonMetrics'),
        hint: i.t('pushes:funnel.soonText'),
        span: 12,
        dashed: true,
        tone: 'warning',
    })

    if (funnelState === 'before') metrics.push({
        value: i.t('pushes:list.beforeFunnel'),
        label: i.t('pushes:list.noFunnelMetrics'),
        hint: typeof trackingSince === 'number'
            ? i.t('pushes:list.beforeFunnelHint', { date: formatSince(trackingSince) })
            : i.t('pushes:list.beforeFunnelHintNoDate'),
        span: 12,
        dashed: true,
        tone: 'warning',
    })

    if (funnelState === 'stats') {
        const ordersHint = stats
            ? [
                i.t('pushes:funnel.ordersHint'),
                stats.canceled > 0 ? i.t('pushes:funnel.canceled', { value: stats.canceled }) : null,
                stats.orders > 0 ? i.t('pushes:funnel.ordersLinkHint') : null,
            ].filter(Boolean).join('. ')
            : i.t('pushes:list.afterSend')

        metrics.push(
            { value: stats ? stats.taps.toLocaleString() : EMPTY_VALUE, label: i.t('pushes:funnel.taps'), hint: i.t('pushes:funnel.tapsHint'), span: 3 },
            { value: stats ? percent(stats.ctr) : EMPTY_VALUE, label: i.t('pushes:funnel.ctr'), hint: i.t('pushes:funnel.ctrHint'), span: 3 },
            {
                value: stats ? stats.orders.toLocaleString() : EMPTY_VALUE,
                label: i.t('pushes:funnel.orders'),
                hint: ordersHint,
                span: 3,
                // Заказы этой рассылки в общем списке заказов
                href: stats && stats.orders > 0 ? `/shop/orders?push_id=${push.id}` : undefined,
            },
            {
                // В ячейке — целыми, точная сумма в подсказке
                value: stats ? getPriceShort(stats.revenue) : EMPTY_VALUE,
                label: i.t('pushes:funnel.ordersSum'),
                hint: stats ? `${getPrice(stats.revenue)}. ${i.t('pushes:funnel.ordersSumHint')}` : i.t('pushes:funnel.ordersSumHint'),
                span: 3,
            },
        )
    }

    const confirmDelete = () => Modal.confirm({
        title: i.t('def:confirms.delete'),
        content: i.t('def:confirms.this_action'),
        okText: i.t('def:actions.remove'),
        okButtonProps: { danger: true },
        cancelText: i.t('def:no'),
        onOk: () => pushesController.delete(push.id),
    })

    const menu: MenuProps['items'] = [
        ...(editable ? [{ key: 'edit', icon: <EditOutlined />, label: <a href={`/pushes/${push.id}`}>{i.t('def:actions.edit')}</a> }] : []),
        { key: 'delete', icon: <DeleteOutlined />, danger: true, label: i.t('def:actions.remove'), onClick: confirmDelete },
    ]

    return (
        <ListCard
            href={editable ? `/pushes/${push.id}` : undefined}
            // Миниатюра — только у пушей с картинкой: пустая иконка лишь отнимала место
            media={image ? <ListMedia image={image} /> : undefined}
            title={push.title}
            titleHint={push.title}
            text={push.description}
            chips={<>
                {scheduled
                    ? <ListChip tone="warning" icon={<ClockCircleFilled />}>{i.t('pushes:list.scheduled')}</ListChip>
                    : <ListChip tone="success" icon={<CheckCircleFilled />}>{i.t('pushes:list.sent')}</ListChip>
                }
                <ListChip tooltip={i.t('pushes:sendTimeProject', { timezone: pushesController.projectTimezone })}>{moment_custom(pushesController.wallClockMs(push.postpone_to), 'D MMM YYYY, HH:mm')}</ListChip>
                <DevicesChip devices={push.devices} />
                {link && <ListChip icon={<LinkOutlined />}>{link}</ListChip>}
                {push.bonus_amount > 0 &&
                    <ListChip tone="primary" icon={<GiftOutlined />}>
                        {i.t('pushes:bonus.chip', { amount: push.bonus_amount, days: push.bonus_days })}
                    </ListChip>
                }
            </>}
            metrics={metrics}
            metricColumns={12}
            metricWidth={36}
            actions={
                <Dropdown menu={{ items: menu }} trigger={['click']} placement="bottomRight">
                    <Button type="text" shape="circle" icon={<MoreOutlined />} aria-label={i.t('def:actions.actions')} />
                </Dropdown>
            }
        />
    )
})

export const Pushes = observer(() => {

    useEffect(() => {
        pushesController.loadTimezone()
        pushesController.get()
        pushesFunnel.getSummary()
    }, [])

    // Цифры воронки — по рассылкам текущей страницы
    const pageIds = pushesController.list.list.map((push: any) => push.id).join(',')

    useEffect(() => {
        pushesFunnel.getPushItems(pageIds ? pageIds.split(',').map(Number) : [])
    }, [pageIds])

    const buttons = [
        { title: i.t('pushes:templates.title'), href: `/pushes/templates`, type: 'dashed' },
        { title: i.t('def:actions.create'), href: `/pushes/0`, type: 'primary' }
    ]

    const { list } = pushesController
    const page = Math.floor(list.offset / list.limit) + 1

    return (
        <>
            <Titles
                title={i.t('pushes:pushes.title')}
                buttons={buttons}
            />

            <div className="pu-page">
                <PushFunnelSummary funnel={pushesFunnel} />

                <CardList
                    title={i.t('pushes:list.title')}
                    count={list.count}
                    items={list.list}
                    loading={pushesController.loading}
                    renderItem={(push: any) => <PushCard push={push} />}
                    empty={{
                        icon: <BellFilled />,
                        title: i.t('pushes:list.empty'),
                        text: i.t('pushes:list.emptyHint'),
                        action: <Button type="primary" icon={<PlusOutlined />} href="/pushes/0">{i.t('pushes:list.create')}</Button>,
                    }}
                    pagination={{
                        current: page,
                        total: list.count,
                        pageSize: list.limit,
                        showSizeChanger: true,
                        onChange: (next, size) => {
                            if (size !== list.limit) {
                                list.limit = size
                                list.offset = 0
                            } else {
                                list.offset = (next - 1) * list.limit
                            }
                            pushesController.get()
                        }
                    }}
                />
            </div>
        </>
    )
})
